import React from "react";
import { useTranslation } from "react-i18next";

import { useAllTranslatedStandards } from "hooks/useStandards";

interface ConformityStandardsSelectorProps {
  /** Standard codes the declaration would list, e.g. "EN ISO 12100:2010". */
  standards: string[];
  /** Codes the user has deselected. */
  excludedStandards: string[];
  onToggle: (standard: string) => void;
}

/**
 * Checkbox list of the standards that appear in the declaration of conformity.
 *
 * Lives in its own component so the standards catalogue is only fetched once
 * the dialog is actually open — Headless UI unmounts modal content while it is
 * closed, so the hooks below do not run until then.
 */
export const ConformityStandardsSelector: React.FC<
  ConformityStandardsSelectorProps
> = ({ standards, excludedStandards, onToggle }) => {
  const { t } = useTranslation();
  const { translations, isLoading } = useAllTranslatedStandards();

  // The catalogue is keyed on the part before the first colon, matching how
  // the renderer resolves a standard's title in Conformity.tsx.
  const titleFor = (standard: string): string | undefined => {
    const [name] = standard.split(":");

    return translations?.find((entry) => entry.name === name)?.content;
  };

  if (standards.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <h4 className="text-sm font-medium text-gray-700 mb-1">
        {t("documents.generate.conformity_standards")}
      </h4>
      <p className="text-xs text-gray-500 mb-2">
        {t("documents.generate.conformity_standards_info")}
      </p>
      <div className="bg-gray-50 rounded-md p-3 text-sm max-h-56 overflow-y-auto">
        {isLoading && (
          <p className="italic text-gray-500 text-xs pb-2">
            {t("documents.generate.standards_loading")}
          </p>
        )}
        {standards.map((standard) => {
          const title = titleFor(standard);

          return (
            <div key={standard} className="flex items-start py-1">
              <input
                id={`standard-${standard}`}
                type="checkbox"
                checked={!excludedStandards.includes(standard)}
                onChange={() => onToggle(standard)}
                className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`standard-${standard}`}
                className="ml-2 block cursor-pointer"
              >
                <span className="block text-sm text-gray-900">
                  {standard.replace(/:$/, "")}
                </span>
                {title && (
                  <span className="block text-xs text-gray-500">{title}</span>
                )}
              </label>
            </div>
          );
        })}
      </div>
      {excludedStandards.length > 0 && (
        <p className="mt-1 text-xs text-amber-700">
          {t("documents.generate.standards_excluded_count", {
            count: excludedStandards.length,
          })}
        </p>
      )}
    </div>
  );
};
